import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { fetchParentCheckoutRequests, fetchParentProfile, parentAddChild, updateParentProfile, updateChild } from "@/api";
import type { ApiCheckoutRequest, ApiParentProfile } from "@/api/types";

interface Notification {
  id: string;
  childName: string;
  title: string;
  message: string;
  date: string;
  read: boolean;
  type: "milestone" | "assessment" | "tip";
}

export default function AccountPage() {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const isRTL = i18n.language === "ar";

  const [profile, setProfile] = useState<ApiParentProfile | null>(null);
  const [checkoutRequests, setCheckoutRequests] = useState<ApiCheckoutRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"children" | "reports" | "orders" | "notifications">("children");
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [showAddChildModal, setShowAddChildModal] = useState(false);

  // Add child form states
  const [newChildName, setNewChildName] = useState("");
  const [newChildDob, setNewChildDob] = useState("");
  const [newChildGender, setNewChildGender] = useState("male");
  const [addingChild, setAddingChild] = useState(false);

  // Edit parent profile states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState("");

  // Edit child form states
  const [showEditChildModal, setShowEditChildModal] = useState(false);
  const [editingChild, setEditingChild] = useState<any | null>(null);
  const [editChildName, setEditChildName] = useState("");
  const [editChildDob, setEditChildDob] = useState("");
  const [editChildGender, setEditChildGender] = useState("male");
  const [savingChild, setSavingChild] = useState(false);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const [data, requests] = await Promise.all([
        fetchParentProfile(),
        fetchParentCheckoutRequests().catch(() => []),
      ]);
      setProfile(data);
      setCheckoutRequests(requests);
    } catch (err: any) {
      console.error("Error fetching parent profile:", err);
      // Unauthorized or token expired
      localStorage.removeItem("parent_token");
      localStorage.removeItem("parent_name");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("parent_token");
    if (!token) {
      navigate("/");
      return;
    }
    loadProfile();
  }, []);

  const handleAddChild = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newChildName.trim() || !newChildDob) {
      alert(t("requiredFieldsMissing"));
      return;
    }

    setAddingChild(true);
    try {
      await parentAddChild({
        name: newChildName,
        dob: newChildDob,
        gender: newChildGender,
      });
      setNewChildName("");
      setNewChildDob("");
      setNewChildGender("male");
      setShowAddChildModal(false);
      await loadProfile();
    } catch (err) {
      console.error("Error adding child:", err);
      alert(t("addChildFailed"));
    } finally {
      setAddingChild(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editName.trim() || !editEmail.trim() || !editPhone.trim()) {
      setProfileError(t("profileFieldsRequired"));
      return;
    }
    setSavingProfile(true);
    setProfileError("");
    try {
      await updateParentProfile({
        name: editName,
        email: editEmail,
        phoneNumber: editPhone,
      });
      localStorage.setItem("parent_name", editName);
      setIsEditingProfile(false);
      await loadProfile();
    } catch (err: any) {
      console.error(err);
      setProfileError(t("emailOrPhoneAlreadyUsed"));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleUpdateChild = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingChild) return;
    if (!editChildName.trim() || !editChildDob) {
      alert(t("requiredFieldsMissing"));
      return;
    }

    setSavingChild(true);
    try {
      await updateChild(editingChild.id, {
        name: editChildName,
        dob: editChildDob,
        gender: editChildGender,
      });
      setShowEditChildModal(false);
      setEditingChild(null);
      await loadProfile();
    } catch (err) {
      console.error("Error updating child:", err);
      alert(t("updateChildFailed"));
    } finally {
      setSavingChild(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "excellent": return "#2A9D8F";
      case "good": return "#E9C46A";
      case "developing": return "#F4A261";
      default: return "#E8927C";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "excellent": return t("excellent");
      case "good": return t("good");
      case "developing": return t("developing");
      default: return t("needsAttention");
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "milestone": return "ri-trophy-line";
      case "assessment": return "ri-calendar-check-line";
      case "tip": return "ri-lightbulb-line";
      default: return "ri-notification-line";
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case "milestone": return "bg-[#E9C46A]/10 text-[#E9C46A]";
      case "assessment": return "bg-primary-400/10 text-primary-400";
      case "tip": return "bg-teal-400/10 text-teal-500";
      default: return "bg-gray-100 text-gray-500";
    }
  };

  const getAgeGroupLabel = (id?: number) => {
    if (!id) return t("customAssessment");
    switch (id) {
      case 1: return t("age0_6");
      case 2: return t("age7_12");
      case 3: return t("age1_2");
      case 4: return t("age2_3");
      case 5: return t("age3_4");
      case 6: return t("age4_5");
      case 7: return t("age5_6");
      default: return t("customAssessment");
    }
  };

  const formatRequestDate = (value: string) => {
    return new Date(value).toLocaleDateString(isRTL ? "ar-SA" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getRequestStatusLabel = (status: string) => {
    switch (status) {
      case "salla_order_created": return t("accountOrderStatusStoreOrderCreated");
      case "pending": return t("accountOrderStatusPending");
      case "completed": return t("accountOrderStatusCompleted");
      case "cancelled": return t("accountOrderStatusCancelled");
      default: return t("accountOrderStatusUnknown");
    }
  };

  // Compile reports across all children
  const reportsList = profile
    ? profile.children.flatMap((c) =>
        c.reports.map((r) => {
          const overallScore = Math.round((r.scorePhysical + r.scoreCognitive + r.scoreSocial) / 3);
          let status = "needsAttention";
          if (overallScore >= 80) status = "excellent";
          else if (overallScore >= 60) status = "good";
          else if (overallScore >= 40) status = "developing";

          return {
            id: r.id,
            childId: c.id,
            childName: c.name,
            ageGroupId: r.ageGroupId,
            date: new Date(r.createdAt).toLocaleDateString(isRTL ? "ar-SA" : "en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            }),
            overallScore,
            status,
          };
        })
      ).sort((a, b) => b.overallScore - a.overallScore)
    : [];

  const filteredReports = selectedChildId
    ? reportsList.filter((r) => r.childId === selectedChildId)
    : reportsList;

  // Generate dynamic developmental tips/notifications based on child profile
  const notificationsList: Notification[] = [];
  if (profile) {
    profile.children.forEach((c) => {
      notificationsList.push({
        id: `n-tip-${c.id}`,
        childName: c.name,
        title: t("recommendedGrowthTip"),
        message: t("recommendedGrowthTipMessage", { name: c.name }),
        date: new Date().toLocaleDateString(isRTL ? "ar-SA" : "en-US", { month: "short", day: "numeric" }),
        read: false,
        type: "tip",
      });

      if (c.reports.length > 0) {
        const lastReport = c.reports[0];
        const overallScore = Math.round((lastReport.scorePhysical + lastReport.scoreCognitive + lastReport.scoreSocial) / 3);
        if (overallScore >= 80) {
          notificationsList.push({
            id: `n-milestone-${c.id}`,
            childName: c.name,
            title: t("amazingMilestone"),
            message: t("amazingMilestoneMessage", { name: c.name }),
            date: new Date(lastReport.createdAt).toLocaleDateString(isRTL ? "ar-SA" : "en-US", { month: "short", day: "numeric" }),
            read: false,
            type: "milestone",
          });
        }
      }
    });
  }

  const filteredNotifications = selectedChildId
    ? notificationsList.filter((n) => n.childName === profile?.children.find((c) => c.id === selectedChildId)?.name)
    : notificationsList;

  const unreadCount = notificationsList.filter((n) => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFBF7]">
        <div className="text-center text-gray-500">{t("loading")}</div>
      </div>
    );
  }

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-[#FFFBF7] pt-20 md:pt-24 pb-8">
      <div className="w-full px-4 md:px-8 lg:px-16 max-w-5xl mx-auto">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl p-5 md:p-8 border border-gray-100 shadow-sm mb-6">
          <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-6">
            <div className="relative">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-primary-400/10 flex items-center justify-center border-4 border-primary-400/20">
                <i className="ri-user-smile-line text-primary-400 text-4xl" />
              </div>
              <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-primary-400 flex items-center justify-center">
                <i className="ri-user-line text-white text-sm" />
              </div>
            </div>
            {isEditingProfile ? (
              <form onSubmit={handleUpdateProfile} className="text-start flex-1 w-full space-y-3">
                {profileError && (
                  <div className="p-2 bg-red-50 text-red-500 rounded-lg text-xs flex items-center gap-1">
                    <i className="ri-error-warning-line" />
                    <span>{profileError}</span>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t("name")}</label>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 outline-none text-sm text-start"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t("email")}</label>
                    <input
                      type="email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 outline-none text-sm text-start"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t("phoneNumber")}</label>
                    <input
                      type="text"
                      value={editPhone}
                      onChange={(e) => setEditPhone(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 outline-none text-sm text-start"
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end pt-1">
                  <button
                    type="button"
                    onClick={() => setIsEditingProfile(false)}
                    className="px-3 py-1.5 border border-gray-200 hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-medium transition-colors"
                  >
                    {t("cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={savingProfile}
                    className="px-3 py-1.5 bg-primary-400 hover:bg-primary-500 disabled:bg-gray-300 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                  >
                    {savingProfile ? (
                      <span>{t("saving")}</span>
                    ) : (
                      <>
                        <i className="ri-save-line" />
                        <span>{t("save")}</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-center sm:text-start flex-1">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                  <h1 className="text-xl md:text-2xl font-bold text-navy-400">{profile.name}</h1>
                  <button
                    onClick={() => {
                      setEditName(profile.name);
                      setEditEmail(profile.email);
                      setEditPhone(profile.phoneNumber);
                      setProfileError("");
                      setIsEditingProfile(true);
                    }}
                    className="text-xs text-primary-400 hover:text-primary-500 font-medium flex items-center gap-1 self-center sm:self-auto"
                  >
                    <i className="ri-edit-line" />
                    <span>{t("editDetails")}</span>
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-1 flex items-center justify-center sm:justify-start gap-1">
                  <i className="ri-mail-line opacity-75 text-xs" />
                  {profile.email}
                </p>
                <p className="text-sm text-gray-400 flex items-center justify-center sm:justify-start gap-1">
                  <i className="ri-phone-line opacity-75 text-xs" />
                  {profile.phoneNumber}
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => setShowAddChildModal(true)}
                className="px-4 py-2 bg-primary-400 hover:bg-primary-500 text-white rounded-xl text-sm font-medium transition-colors flex items-center gap-2"
              >
                <i className="ri-add-line" />
                {t("addChild")}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => { setActiveTab("children"); setSelectedChildId(null); }}
              className={`flex-1 py-4 text-sm font-semibold transition-colors relative ${
                activeTab === "children" ? "text-primary-400" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <i className="ri-parent-line" />
                {t("myChildren")}
                <span className="bg-primary-400/10 text-primary-400 text-xs px-2 py-0.5 rounded-full">
                  {profile.children.length}
                </span>
              </span>
              {activeTab === "children" && (
                <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary-400 rounded-full" />
              )}
            </button>
            <button
              onClick={() => { setActiveTab("reports"); setSelectedChildId(null); }}
              className={`flex-1 py-4 text-sm font-semibold transition-colors relative ${
                activeTab === "reports" ? "text-primary-400" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <i className="ri-file-chart-line" />
                {t("reports")}
                <span className="bg-primary-400/10 text-primary-400 text-xs px-2 py-0.5 rounded-full">
                  {reportsList.length}
                </span>
              </span>
              {activeTab === "reports" && (
                <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary-400 rounded-full" />
              )}
            </button>
            <button
              onClick={() => { setActiveTab("orders"); setSelectedChildId(null); }}
              className={`flex-1 py-4 text-sm font-semibold transition-colors relative ${
                activeTab === "orders" ? "text-primary-400" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <i className="ri-file-list-3-line" />
                {t("myOrders")}
                <span className="bg-primary-400/10 text-primary-400 text-xs px-2 py-0.5 rounded-full">
                  {checkoutRequests.length}
                </span>
              </span>
              {activeTab === "orders" && (
                <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary-400 rounded-full" />
              )}
            </button>
            <button
              onClick={() => { setActiveTab("notifications"); setSelectedChildId(null); }}
              className={`flex-1 py-4 text-sm font-semibold transition-colors relative ${
                activeTab === "notifications" ? "text-primary-400" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <i className="ri-notification-3-line" />
                {t("notifications")}
                {unreadCount > 0 && (
                  <span className="bg-red-400 text-white text-xs px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </span>
              {activeTab === "notifications" && (
                <div className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary-400 rounded-full" />
              )}
            </button>
          </div>

          <div className="p-4 md:p-6">
            {/* Children Filter */}
            {(activeTab === "reports" || activeTab === "notifications") && profile.children.length > 0 && (
              <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-2">
                <button
                  onClick={() => setSelectedChildId(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    selectedChildId === null
                      ? "bg-primary-400 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {t("allChildren")}
                </button>
                {profile.children.map((child) => (
                  <button
                    key={child.id}
                    onClick={() => setSelectedChildId(child.id)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                      selectedChildId === child.id
                        ? "bg-primary-400 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    <i className={child.gender === "male" ? "ri-men-line text-blue-500" : "ri-women-line text-pink-500"} />
                    {child.name}
                  </button>
                ))}
              </div>
            )}

            {/* Children Tab */}
            {activeTab === "children" && (
              <div className="space-y-4">
                {profile.children.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-parent-line text-gray-400 text-2xl" />
                    </div>
                    <p className="text-sm text-gray-500">
                      {t("noChildrenAdded")}
                    </p>
                  </div>
                ) : (
                  profile.children.map((child) => (
                    <div
                      key={child.id}
                      className="bg-gray-50 rounded-xl p-4 md:p-5 flex items-center gap-4 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-white flex items-center justify-center shadow-sm border border-gray-100">
                        <i className={child.gender === "male" ? "ri-men-line text-blue-500 text-2xl" : "ri-women-line text-pink-500 text-2xl"} />
                      </div>
                      <div className="flex-1 min-w-0 text-start">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-base font-bold text-navy-400">{child.name}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            child.gender === "male" ? "bg-blue-50 text-blue-500" : "bg-pink-50 text-pink-500"
                          }`}>
                            {child.gender === "male" ? t("male") : t("female")}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mb-1">
                          {t("born")}: {new Date(child.dob).toLocaleDateString(isRTL ? "ar-SA" : "en-US")} - {t("age")}: {calculateAge(child.dob, t)}
                        </p>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <i className="ri-file-chart-line" />
                            {child.reports?.length || 0} {t("reports")}
                          </span>
                          {child.reports && child.reports.length > 0 && (
                            <span className="text-xs text-gray-400 flex items-center gap-1">
                              <i className="ri-time-line" />
                              {t("lastAssessment")}: {new Date(child.reports[0].createdAt).toLocaleDateString(isRTL ? "ar-SA" : "en-US")}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingChild(child);
                            setEditChildName(child.name);
                            setEditChildDob(child.dob.split("T")[0]);
                            setEditChildGender(child.gender);
                            setShowEditChildModal(true);
                          }}
                          className="p-2 border border-gray-200 hover:bg-gray-50 text-gray-500 hover:text-navy-400 rounded-lg text-xs transition-colors flex items-center justify-center"
                          title={t("editChildInfo")}
                        >
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <button
                          onClick={() => {
                            navigate(`/`);
                          }}
                          className="px-3 py-2 bg-primary-400 hover:bg-primary-500 text-white rounded-lg text-xs font-medium transition-colors flex items-center gap-1"
                        >
                          <i className="ri-add-line" />
                          {t("newAssessment")}
                        </button>
                      </div>
                    </div>
                  ))
                )}

                {/* Add Child Button */}
                <button
                  onClick={() => setShowAddChildModal(true)}
                  className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-primary-400 hover:text-primary-400 transition-colors flex items-center justify-center gap-2"
                >
                  <i className="ri-add-circle-line text-lg" />
                  <span className="text-sm font-medium">{t("addChild")}</span>
                </button>
              </div>
            )}

            {/* Reports Tab */}
            {activeTab === "reports" && (
              <div className="space-y-3">
                {filteredReports.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-file-chart-line text-gray-400 text-2xl" />
                    </div>
                    <p className="text-sm text-gray-500">{t("noReportsYet")}</p>
                  </div>
                ) : (
                  filteredReports.map((report) => (
                    <div
                      key={report.id}
                      className="bg-gray-50 rounded-xl p-4 flex items-center gap-4 hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => navigate(`/report/${report.id}`)}
                    >
                      <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-sm">
                        <i className="ri-bar-chart-grouped-line text-primary-400 text-lg" />
                      </div>
                      <div className="flex-1 min-w-0 text-start">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-bold text-navy-400">{report.childName}</h4>
                          <span
                            className="text-xs px-2 py-0.5 rounded-full font-medium"
                            style={{
                              backgroundColor: `${getStatusColor(report.status)}15`,
                              color: getStatusColor(report.status),
                            }}
                          >
                            {getStatusLabel(report.status)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          {getAgeGroupLabel(report.ageGroupId)} · {report.date}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-navy-400">{report.overallScore}%</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === "orders" && (
              <div className="space-y-3">
                {checkoutRequests.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-file-list-3-line text-gray-400 text-2xl" />
                    </div>
                    <p className="text-sm text-gray-500">{t("noOrdersYet")}</p>
                  </div>
                ) : (
                  checkoutRequests.map((request) => (
                    <div key={request.id} className="bg-gray-50 rounded-xl p-4 text-start">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="px-2.5 py-1 rounded-full bg-white text-primary-500 text-xs font-semibold border border-primary-400/10">
                              {request.type === "store_order" ? t("accountOrderTypeStore") : t("accountOrderTypeInternal")}
                            </span>
                            <span className="px-2.5 py-1 rounded-full bg-white text-gray-500 text-xs font-semibold border border-gray-100">
                              {getRequestStatusLabel(request.status)}
                            </span>
                          </div>
                          <h4 className="text-sm font-bold text-navy-400">
                            {request.childName || t("accountOrderUnknownChild")}
                          </h4>
                          <p className="text-xs text-gray-400 mt-1">{formatRequestDate(request.createdAt)}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm min-w-[180px]">
                          <div>
                            <p className="text-[11px] text-gray-400">{t("accountOrderAmount")}</p>
                            <p className="font-bold text-navy-400">{request.totalAmount || "0"} {t("sar")}</p>
                          </div>
                          <div>
                            <p className="text-[11px] text-gray-400">{t("accountOrderItems")}</p>
                            <p className="font-bold text-navy-400">{request.itemsCount}</p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {(request.products || []).map((product) => (
                          <div key={`${request.id}-${product.sallaProductId}`} className="flex items-center gap-3 rounded-lg bg-white border border-gray-100 p-2.5">
                            {product.imageUrl ? (
                              <img src={product.imageUrl} alt="" className="w-12 h-12 rounded-lg object-cover bg-gray-50" />
                            ) : (
                              <span className="w-12 h-12 rounded-lg bg-gray-50 flex items-center justify-center text-gray-300">
                                <i className="ri-image-line text-lg" />
                              </span>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold text-navy-400 truncate">{product.name || product.sallaProductId}</p>
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {product.quantity} x {product.price || 0} {t("sar")}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>

                      {(request.storeOrderCheckoutUrl || request.storeOrderCustomerUrl) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {request.storeOrderCheckoutUrl && (
                            <a href={request.storeOrderCheckoutUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-2 rounded-lg bg-primary-400 text-white text-xs font-semibold hover:bg-primary-500">
                              {t("accountOrderPaymentLink")}
                            </a>
                          )}
                          {request.storeOrderCustomerUrl && (
                            <a href={request.storeOrderCustomerUrl} target="_blank" rel="noopener noreferrer" className="px-3 py-2 rounded-lg bg-white text-navy-400 border border-gray-100 text-xs font-semibold hover:bg-gray-50">
                              {t("accountOrderDetailsLink")}
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Notifications Tab */}
            {activeTab === "notifications" && (
              <div className="space-y-3">
                {filteredNotifications.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-notification-off-line text-gray-400 text-2xl" />
                    </div>
                    <p className="text-sm text-gray-500">{t("noNotifications")}</p>
                  </div>
                ) : (
                  filteredNotifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`rounded-xl p-4 flex items-start gap-3 transition-colors ${
                        notif.read ? "bg-gray-50" : "bg-primary-400/5 border border-primary-400/10"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getNotificationColor(notif.type)}`}>
                        <i className={`${getNotificationIcon(notif.type)} text-lg`} />
                      </div>
                      <div className="flex-1 min-w-0 text-start">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-bold text-navy-400">{notif.title}</h4>
                          <span className="text-xs text-gray-400">· {notif.childName}</span>
                          {!notif.read && (
                            <span className="w-2 h-2 rounded-full bg-primary-400 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{notif.message}</p>
                        <p className="text-xs text-gray-400 mt-1">{notif.date}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Child Modal */}
      {showAddChildModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowAddChildModal(false)} />
          <form
            onSubmit={handleAddChild}
            className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-navy-400">{t("addChild")}</h2>
              <button
                type="button"
                onClick={() => setShowAddChildModal(false)}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">{t("childName")}</label>
                <input
                  type="text"
                  required
                  value={newChildName}
                  onChange={(e) => setNewChildName(e.target.value)}
                  placeholder={t("childNamePlaceholder")}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm text-start"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">{t("dob")}</label>
                  <input
                    type="date"
                    required
                    value={newChildDob}
                    onChange={(e) => setNewChildDob(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm text-start"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">{t("gender")}</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setNewChildGender("male")}
                      className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${
                        newChildGender === "male"
                          ? "border-primary-400 bg-primary-400/10 text-primary-500"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <i className="ri-men-line mr-1" />{t("male")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewChildGender("female")}
                      className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${
                        newChildGender === "female"
                          ? "border-primary-400 bg-primary-400/10 text-primary-500"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <i className="ri-women-line mr-1" />{t("female")}
                    </button>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                disabled={addingChild}
                className="w-full py-3.5 bg-primary-400 hover:bg-primary-500 disabled:bg-gray-300 text-white rounded-xl font-semibold text-sm transition-all"
              >
                {addingChild ? t("adding") : t("addChild")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Child Modal */}
      {showEditChildModal && editingChild && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setShowEditChildModal(false); setEditingChild(null); }} />
          <form
            onSubmit={handleUpdateChild}
            className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-navy-400">{t("editChildInfo")}</h2>
              <button
                type="button"
                onClick={() => { setShowEditChildModal(false); setEditingChild(null); }}
                className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">{t("childName")}</label>
                <input
                  type="text"
                  required
                  value={editChildName}
                  onChange={(e) => setEditChildName(e.target.value)}
                  placeholder={t("childNamePlaceholder")}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm text-start"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">{t("dob")}</label>
                  <input
                    type="date"
                    required
                    value={editChildDob}
                    onChange={(e) => setEditChildDob(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 outline-none text-sm text-start"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5 text-start">{t("gender")}</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setEditChildGender("male")}
                      className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${
                        editChildGender === "male"
                          ? "border-primary-400 bg-primary-400/10 text-primary-500"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <i className="ri-men-line mr-1" />{t("male")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditChildGender("female")}
                      className={`flex-1 py-3 rounded-xl border text-sm font-medium transition-all ${
                        editChildGender === "female"
                          ? "border-primary-400 bg-primary-400/10 text-primary-500"
                          : "border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <i className="ri-women-line mr-1" />{t("female")}
                    </button>
                  </div>
                </div>
              </div>
              <button
                type="submit"
                disabled={savingChild}
                className="w-full py-3.5 bg-primary-400 hover:bg-primary-500 disabled:bg-gray-300 text-white rounded-xl font-semibold text-sm transition-all"
              >
                {savingChild ? t("saving") : t("saveChanges")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function calculateAge(dob: string, t: (key: string) => string): string {
  if (!dob) return "";
  const birth = new Date(dob);
  const now = new Date();
  const months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years > 0) {
    return `${years} ${years === 1 ? t("yearSingular") : t("yearPlural")} ${remainingMonths > 0 ? `${t("and")} ${remainingMonths} ${t("monthPlural")}` : ""}`;
  }
  return `${months} ${months === 1 ? t("monthSingular") : t("monthPlural")}`;
}
